/**
 * When several nodes stack in one Sankey column, proportional bar heights can sum to more than
 * {@link chartHeight}. Recharts-free {@link SimpleSankey} centers the column in the chart area,
 * which clips top/bottom if the stack is too tall. This scales each column independently so the
 * stack fits (uniform scale per column preserves relative bar sizes).
 */

export type FitSankeyColumnNodeHeightsParams = {
  nodesByLevel: ReadonlyMap<number, readonly number[]>;
  /** In-out: bar lengths; mutated in place for callers that already own the array. */
  nodeSizes: number[];
  chartHeight: number;
  /** Vertical gap between stacked nodes in the same column ({@link SimpleSankey} `effectiveNodePadding`). */
  nodePadding: number;
  /**
   * Max fraction of {@link chartHeight} a column may occupy after scaling.
   * Slightly below 1 avoids sub-pixel clipping against the inner chart box.
   */
  maxFillRatio?: number;
};

export function fitSankeyColumnNodeHeights(params: FitSankeyColumnNodeHeightsParams): void {
  const {
    nodesByLevel,
    nodeSizes,
    chartHeight,
    nodePadding,
    maxFillRatio = 0.97,
  } = params;
  const cap = Math.max(1, chartHeight * maxFillRatio);

  nodesByLevel.forEach((indices) => {
    if (indices.length === 0) return;
    const paddingTotal = nodePadding * (indices.length - 1);
    let sum = 0;
    for (const i of indices) {
      sum += nodeSizes[i];
    }
    const total = sum + paddingTotal;
    if (total <= cap) return;
    const available = Math.max(0, cap - paddingTotal);
    if (sum <= 0 || available <= 0) return;
    const scale = available / sum;
    for (const i of indices) {
      nodeSizes[i] *= scale;
    }
  });
}
